/**
 * Фиксированная точка для цен и размеров — зеркало `services/internal/money`.
 *
 * Правило проекта: цены и размеры НИКОГДА не живут во float. С провода они
 * приходят строками (`api/types.ts`), и `Number(price)` для расчётов запрещён:
 * накопленная ошибка даёт цену, не кратную tickSize, а такой ордер CLOB
 * отклоняет целиком. Внутреннее представление — целые микро-единицы, как в Go.
 *
 * ГРАНИЦА ТОЧНОСТИ, и она уже́, чем в Go. Числа в JS — это double, целые в них
 * точны до `Number.MAX_SAFE_INTEGER` = 9_007_199_254_740_991 микро, то есть до
 * ≈ 9.0·10⁹ единиц. Go считает в int64 и доходит до ≈ 9.2·10¹² единиц, то есть
 * втрое-с-лишним по порядку дальше. Значение с провода крупнее 2⁵³ здесь не
 * округляется молча, а ОТВЕРГАЕТСЯ (`parse` возвращает null): неверная цена,
 * поданная как верная, хуже отсутствующей.
 *
 * Реальные величины стакана Polymarket до этой границы не достают и близко
 * (цена — доля от 1.0, размеры — тысячи share), так что отказ здесь — это
 * сигнал «пришло что-то не то», а не рабочий режим.
 */

/** Микро-единиц в одной целой единице. Совпадает с money.Scale. */
export const SCALE = 1_000_000;

/** Знаков после запятой, соответствующее SCALE. Совпадает с money.Decimals. */
export const DECIMALS = 6;

/** Величина в микро-единицах. Целое число. */
export type Micro = number;

/**
 * Грамматика повторяет money.Parse: необязательный знак, целая часть и до шести
 * знаков дроби. «5.» и «1.2345678» отвергаются там — отвергаются и здесь, иначе
 * два конца одного контракта разошлись бы в том, что считать валидным.
 */
const DECIMAL = /^[+-]?(?:\d+(?:\.\d{1,6})?|\.\d{1,6})$/;

/**
 * Разбор десятичной строки в микро-единицы. null — если строка не число,
 * слишком точна или не влезает в безопасный диапазон.
 *
 * Возвращает null, а не бросает: на входе внешние данные шины, и одно битое
 * значение не должно ронять отрисовку всего стакана.
 */
export function parse(s: string | null | undefined): Micro | null {
  if (s === null || s === undefined) return null;
  const trimmed = s.trim();
  if (!DECIMAL.test(trimmed)) return null;

  const neg = trimmed.startsWith("-");
  const unsigned = trimmed.replace(/^[+-]/, "");
  const [intPart = "", fracPart = ""] = unsigned.split(".");

  // Дробь дополняется нулями справа до шести знаков: "0.5" -> "500000".
  const micro = `${intPart}${fracPart.padEnd(DECIMALS, "0")}`.replace(/^0+(?=\d)/, "");

  // До 15 знаков значение заведомо безопасно (10^15 - 1 < 2^53 - 1), дальше
  // проверяем через BigInt: Number на 16+ знаках уже округляет, и проверять
  // после преобразования было бы поздно.
  if (micro.length > 15) {
    const big = BigInt(micro);
    if (big > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  }
  const value = Number(micro);
  if (!Number.isSafeInteger(value)) return null;
  return neg ? -value : value;
}

/** Разбор с запасным значением — там, где отсутствие данных не новость. */
export function parseOr(s: string | null | undefined, fallback: Micro): Micro {
  const v = parse(s);
  return v === null ? fallback : v;
}

/** 520000 -> "0.52". Зеркало money.Amount.String(): без хвостовых нулей. */
export function format(m: Micro): string {
  const neg = m < 0;
  const abs = Math.abs(m);
  const whole = Math.trunc(abs / SCALE);
  const frac = String(abs % SCALE).padStart(DECIMALS, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

/** 520000 -> "0.520000". Зеркало money.Amount.Fixed(). */
export function fixed(m: Micro): string {
  return toFixed(m, DECIMALS);
}

/**
 * Ровно n знаков после запятой.
 *
 * Нужен колонке цен в стакане: при переменном числе знаков колонка «пляшет»
 * на каждом обновлении, а стакан смотрят часами.
 */
export function toFixed(m: Micro, n: number): string {
  const digits = Math.max(0, Math.min(DECIMALS, Math.trunc(n)));
  const neg = m < 0;
  const abs = Math.abs(m);
  const whole = Math.trunc(abs / SCALE);
  if (digits === 0) return `${neg ? "-" : ""}${whole}`;
  const frac = String(abs % SCALE).padStart(DECIMALS, "0").slice(0, digits);
  return `${neg ? "-" : ""}${whole}.${frac}`;
}

/**
 * Ровно n знаков после запятой, с ОКРУГЛЕНИЕМ по половине.
 *
 * Второй форматтер рядом с toFixed, а не флаг в нём, потому что правило зависит от
 * того, ЧТО показывают, и подменять его флагом на вызове — способ однажды
 * округлить цену ордера.
 *
 * toFixed обрезает, и для денег это верно: цена, выросшая от форматирования,
 * читается как уровень, которого в стакане нет, а размер — как доступный к
 * продаже объём, которого нет на балансе. Ошибка усечения всегда в безопасную
 * сторону.
 *
 * Для СПРАВОЧНЫХ величин верно обратное. Индекс и страйк ни во что не
 * подставляются, зато сравниваются глазами с цифрами в интерфейсе Polymarket, а
 * тот округляет. Усечение давало здесь систематический сдвиг до одной копейки
 * вниз — 1918.589 показывалось как «1 918.58» против «1 918.59» у них, — и этот
 * цент ложился поверх настоящего расхождения метрик, мешая его разбирать.
 */
export function roundToFixed(m: Micro, n: number): string {
  const digits = Math.max(0, Math.min(DECIMALS, Math.trunc(n)));
  // Округление на ЦЕЛЫХ микро-единицах: перевод в дробное число здесь вернул бы
  // ту самую двоичную дробь, от которой уводит весь модуль.
  const step = 10 ** (DECIMALS - digits);
  const neg = m < 0;
  const abs = Math.abs(m);
  const rounded = Math.round(abs / step) * step;
  return toFixed(neg ? -rounded : rounded, digits);
}

export function add(a: Micro, b: Micro): Micro | null {
  const sum = a + b;
  return Number.isSafeInteger(sum) ? sum : null;
}

export function sub(a: Micro, b: Micro): Micro | null {
  const diff = a - b;
  return Number.isSafeInteger(diff) ? diff : null;
}

export function cmp(a: Micro, b: Micro): -1 | 0 | 1 {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Умножение величин: (a * b) / SCALE, округление половины от нуля — как money.Mul.
 *
 * Быстрый путь через double, медленный через BigInt. Вторая ветка не
 * перестраховка: произведение ДО деления вылезает за 2⁵³ уже на цене 1.0 и
 * размере ~9000 share, то есть на рядовом уровне стакана. Выбор ветки по
 * `x * y` корректен и на неточном double: порядок величины он передаёт верно,
 * а на границе мы просто уходим в заведомо точную ветку.
 */
export function mul(a: Micro, b: Micro): Micro | null {
  const neg = a < 0 !== b < 0;
  const x = Math.abs(a);
  const y = Math.abs(b);

  if (x * y <= Number.MAX_SAFE_INTEGER) {
    const q = Math.trunc((x * y + SCALE / 2) / SCALE);
    return neg ? -q : q;
  }

  const half = BigInt(SCALE / 2);
  const q = (BigInt(x) * BigInt(y) + half) / BigInt(SCALE);
  if (q > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return neg ? -Number(q) : Number(q);
}

/**
 * Деление величин: (a * SCALE) / b, округление ВНИЗ — как money.DivFloor.
 *
 * Вниз, а не к ближайшему (в отличие от `mul`): делением считается размер из
 * суммы денег, и округление вверх потратило бы больше, чем названо.
 *
 * BigInt здесь не «на всякий случай»: a * SCALE вылезает за 2⁵³ уже при сумме
 * около 9·10³ единиц, то есть на девяти тысячах долларов — величине хоть и
 * крупной для скальпинга, но вполне достижимой.
 */
export function div(a: Micro, b: Micro): Micro | null {
  if (b === 0) return null;
  const neg = a < 0 !== b < 0;
  const x = Math.abs(a);
  const y = Math.abs(b);

  let q: number;
  let exact: boolean;
  if (x * SCALE <= Number.MAX_SAFE_INTEGER) {
    const num = x * SCALE;
    q = Math.floor(num / y);
    exact = num % y === 0;
  } else {
    const num = BigInt(x) * BigInt(SCALE);
    const by = BigInt(y);
    const big = num / by;
    if (big > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    q = Number(big);
    exact = num % by === 0n;
  }
  if (!neg) return q;
  // Делили модули, поэтому частное округлилось К НУЛЮ. Для отрицательного
  // результата это округление ВВЕРХ, а нужен пол — как в Go.
  return -(exact ? q : q + 1);
}

/**
 * Сколько знаков после запятой показывать при данном шаге цены: "0.001" -> 3.
 *
 * Берётся из tick_size СТАКАНА, а не рынка: смену шага обрабатывает трейдер
 * (tick_size_change) и кладёт её в снапшот, а справочник рынков может отставать.
 */
export function decimalsForTick(tick: string | null | undefined): number {
  const micro = parse(tick);
  if (micro === null || micro <= 0) return 2;
  const frac = String(micro % SCALE).padStart(DECIMALS, "0").replace(/0+$/, "");
  return frac.length === 0 ? 0 : frac.length;
}

/**
 * Разряды пробелом: «78 565.4».
 *
 * Живёт здесь, а не в ленте, где появился: тем же приёмом печатаются доллары
 * счётчика, объём в share и цена актива на панели плотности, и третья копия
 * одного регулярного выражения разъехалась бы с первыми двумя при первой же
 * правке разделителя.
 */
export function groupThousands(s: string): string {
  const [int = "", frac] = s.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return frac ? `${grouped}.${frac}` : grouped;
}
